import { Suspense } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader2, Settings, LogOut, ChevronDown, Sun, Moon, Laptop } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/components/ThemeProvider";

const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-[50vh]">
    <Loader2 className="h-8 w-8 animate-spin" />
  </div>
);

const Layout = () => {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const isAdmin = user?.role === 'admin';

  const navLinkClass = (isActive: boolean) =>
    cn(
      "rounded-full px-4 py-2 text-sm font-medium transition-all duration-200",
      isActive
        ? "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary"
        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
    );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-slate-50/95 backdrop-blur supports-[backdrop-filter]:bg-slate-50/80 dark:border-border dark:bg-background/95 dark:supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between gap-4">
            <div className="flex items-center gap-6 md:gap-8">
              <Link to="/" className="flex shrink-0 items-center">
                <div
                  className="flex items-center rounded-lg px-3 py-1.5"
                  style={{
                    background: "linear-gradient(145deg, #1a304e 0%, #243c5a 100%)",
                  }}
                >
                  <img
                    src="/logo.png"
                    alt="Navsan Logo"
                    className="h-9 w-auto max-w-[150px] object-contain"
                  />
                </div>
              </Link>

              <nav className="hidden items-center gap-1 md:flex">
                {isAdmin ? (
                  <>
                    <Link
                      to="/admin/dashboard"
                      className={navLinkClass(location.pathname === "/admin/dashboard")}
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/admin/users"
                      className={navLinkClass(location.pathname === "/admin/users")}
                    >
                      Users
                    </Link>
                    <Link
                      to="/leave"
                      className={navLinkClass(location.pathname === "/leave")}
                    >
                      Apply Leave
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/weeklytimesheet"
                      className={navLinkClass(
                        location.pathname === "/weeklytimesheet" ||
                          location.pathname === "/timesheet" ||
                          location.pathname === "/"
                      )}
                    >
                      Weekly Timesheet
                    </Link>
                    <Link
                      to="/leave"
                      className={navLinkClass(location.pathname === "/leave")}
                    >
                      Apply Leave
                    </Link>
                  </>
                )}
              </nav>
            </div>

            <div className="flex items-center gap-3 md:gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-fit px-2 hover:bg-slate-100 dark:hover:bg-accent md:px-3">
                    <div className="mr-2 text-left">
                      <p className="text-sm font-medium leading-none text-foreground">{user?.name}</p>
                      <p className="hidden text-xs text-muted-foreground md:block">{user?.email}</p>
                    </div>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[200px]">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Theme</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => setTheme("light")}>
                    <Sun className="mr-2 h-4 w-4" />
                    <span>Light</span>
                    {theme === "light" && (
                      <span className="ml-auto text-xs text-primary">✓</span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("dark")}>
                    <Moon className="mr-2 h-4 w-4" />
                    <span>Dark</span>
                    {theme === "dark" && (
                      <span className="ml-auto text-xs text-primary">✓</span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("system")}>
                    <Laptop className="mr-2 h-4 w-4" />
                    <span>System</span>
                    {theme === "system" && (
                      <span className="ml-auto text-xs text-primary">✓</span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-red-600" onClick={logout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 py-8">
        <Suspense fallback={<LoadingSpinner />}>
          <Outlet />
        </Suspense>
      </main>

      <footer className="border-t bg-muted/50">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Navsan - Timesheet App. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm">Privacy Policy</Button>
              <Button variant="ghost" size="sm">Terms of Service</Button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;